/* Firebase Cloud Messaging — client helpers.
 *
 * Exports:
 *   initFirebase()        — singleton initializer (no-op after first call)
 *   getMessagingToken()   — asks browser permission + returns the FCM device token
 *   listenForeground(cb)  — calls cb({title, body, url}) when a push arrives while the tab is focused
 *
 * iOS Safari note: Web Push on iOS only works for INSTALLED PWAs on iOS 16.4+.
 * We detect this in `iosNeedsInstall()` and let the UI show the "Add to
 * Home Screen" banner before asking for permission.
 */
import { initializeApp, getApps, getApp } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported } from "firebase/messaging";

const firebaseConfig = {
  apiKey: "AIzaSyCOde1rm0tV60CCxWlpqNrH7-eZ68_tqto",
  authDomain: "konattu-hrms.firebaseapp.com",
  projectId: "konattu-hrms",
  storageBucket: "konattu-hrms.firebasestorage.app",
  messagingSenderId: "641917557860",
  appId: "1:641917557860:web:287a89f0ffa0b7a7d51371",
};

const VAPID_PUBLIC_KEY =
  "BOJsFrype4-jeEaI7jb49pQtd2er0-lS2jRO_AgVM-m410ZxJtRCOYUbxkJtupF-zg5464VPg6zjHApyQH5Jl0Q";

export function initFirebase() {
  if (typeof window === "undefined") return null;
  return getApps().length ? getApp() : initializeApp(firebaseConfig);
}

export function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function isStandalonePWA() {
  if (typeof window === "undefined") return false;
  return (
    (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
    window.navigator.standalone === true
  );
}

/** iOS Safari requires the user to "Add to Home Screen" before Web Push works. */
export function iosNeedsInstall() {
  return isIOS() && !isStandalonePWA();
}

async function _registerFcmSW() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/firebase-messaging-sw.js", {
      scope: "/firebase-cloud-messaging-push-scope",
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[FCM] Failed to register messaging SW", e);
    return null;
  }
}

/**
 * Request notification permission and return the device's FCM token.
 * Resolves to `{ token, status }` where status is one of:
 *   'granted' | 'denied' | 'default' | 'unsupported' | 'ios-needs-install' | 'error'
 */
export async function getMessagingToken() {
  if (typeof window === "undefined") return { token: null, status: "unsupported" };
  if (iosNeedsInstall()) return { token: null, status: "ios-needs-install" };
  if (!(await isSupported().catch(() => false))) {
    return { token: null, status: "unsupported" };
  }
  if (!("Notification" in window)) return { token: null, status: "unsupported" };

  let permission = Notification.permission;
  if (permission === "default") {
    try {
      permission = await Notification.requestPermission();
    } catch {
      permission = "denied";
    }
  }
  if (permission !== "granted") return { token: null, status: permission };

  initFirebase();
  const swReg = await _registerFcmSW();
  try {
    const messaging = getMessaging();
    const token = await getToken(messaging, {
      vapidKey: VAPID_PUBLIC_KEY,
      serviceWorkerRegistration: swReg || undefined,
    });
    return { token: token || null, status: token ? "granted" : "error" };
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[FCM] getToken failed", e);
    return { token: null, status: "error", error: String(e?.message || e) };
  }
}

/** Listen for foreground messages — fires while the tab is focused. */
export function listenForeground(cb) {
  if (typeof window === "undefined") return () => {};
  isSupported()
    .then((ok) => {
      if (!ok) return;
      initFirebase();
      try {
        const messaging = getMessaging();
        onMessage(messaging, (payload) => {
          const data = payload?.notification || payload?.data || {};
          cb({
            title: data.title || "Konattu HRMS",
            body: data.body || "",
            url: (payload?.fcmOptions && payload.fcmOptions.link) || data.url || "/",
          });
        });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[FCM] onMessage hook failed", e);
      }
    })
    .catch(() => {});
  // Caller can use the value but we don't expose an unsubscribe — onMessage
  // returns its own. This is intentional for simplicity.
  return () => {};
}
